import { Checkbox, IconButton, Stack } from '@mui/material';
import { UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT, type UpdateCustomer } from '@weft/types';
import { PlusIcon } from 'apps/app/src/components/icons/PlusIcon';
import { TrashIcon } from 'apps/app/src/components/icons/TrashIcon';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '../../../../components/design/Input/Input';
import { CustomerViewPropertiesBlock } from './CustomerViewPropertiesBlock';

export const CustomerInfoViewEditContacts: React.FC = () => {
  const { setValue, watch, resetField, trigger } = useFormContext<UpdateCustomer>();
  const [isInEditMode, setIsInEditMode] = useState(false);
  const contacts = watch('contacts');

  const handleAddContact = () => {
    const existingContacts = contacts ?? [];
    setValue('contacts', [...existingContacts, {
      name: '',
      email: '',
      isPrimary: false,
    }]);
  };

  const handleCancelClick = () => {
    resetField('contacts');
  };

  const handleSaveClick = () => {
    return trigger(['contacts']);
  };

  return (
    <>
      <CustomerViewPropertiesBlock
        title='Contact'
        childrenContainerClassName='flex-col flex-nowrap gap-8'
        className='pb-6'
        showEditButton
        onEditChange={setIsInEditMode}
        sectionToSubmit={UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.contacts}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
      >
        {contacts?.map((_, index)=>{
          return (
            <CustomerInfoViewEditContactsItem
              key={index}
              contactIndex={index}
              isInEditMode={isInEditMode}
            />
          );
        })}
        <button
          type="button"
          className="text-icon enabled:text-style group flex items-center space-x-1 disabled:cursor-default"
          onClick={handleAddContact}
          disabled={!isInEditMode}
        >
          <PlusIcon className="text-icon enabled:group-hover:text-style h-6 w-6"/>
          <label className="cursor-pointer pl-2 text-sm group-disabled:cursor-default" >
            Add Contact
          </label>
        </button>
      </CustomerViewPropertiesBlock>
    </>
  );
};

type CustomerInfoViewEditContactsItemProps = {
  contactIndex: number;
  isInEditMode: boolean;
};

const CustomerInfoViewEditContactsItem: React.FC<CustomerInfoViewEditContactsItemProps> = ({ contactIndex, isInEditMode }) => {
  const { getValues, getFieldState, register, setValue } = useFormContext<UpdateCustomer>();

  const handleDeleteContact = () => { 
    const contacts = getValues('contacts') ?? [];
    const updatedContacts = contacts.filter((_, index) => index !== contactIndex);
    setValue('contacts', updatedContacts);
  };

  const handlerIsPrimaryChange = (isChecked: boolean) => {
    const contacts = getValues('contacts') ?? [];
    if (!isChecked) {
      setValue(`contacts.${contactIndex}.isPrimary`, isChecked);
      
      return;
    }

    const newContracts = contacts.map((contract, index) => ({ ...contract, isPrimary: index === contactIndex }));
    setValue('contacts', newContracts);
  };

  const nameRegister = vyFormRegister(register, `contacts.${contactIndex}.name`);
  const emailRegister = vyFormRegister(register, `contacts.${contactIndex}.email`);

  return (
    <div
      className='flex'
    >
      <div className="flex w-2/5 flex-col border-0 pr-6">
        <Input
          label='Contact Name'
          {...nameRegister}
          debounce={false}
          isInEditModeOverride={isInEditMode}
          editModeOverride
          defaultValue={getValues(`contacts.${contactIndex}.name`)}
          errorMessage={getFieldState(`contacts.${contactIndex}.name`)?.error?.message}
          editMode
          placeholder="Text here"
        />
      </div>

      <div className="flex w-2/5 flex-col border-0 pr-6">
        <Input
          label='eMail'
          {...emailRegister}
          debounce={false}
          isInEditModeOverride={isInEditMode}
          editModeOverride
          defaultValue={getValues(`contacts.${contactIndex}.email`)}
          editMode
          errorMessage={getFieldState(`contacts.${contactIndex}.email`)?.error?.message}
          placeholder="Text here"
        />
      </div>
      <div className="flex w-1/5 flex-col border-0 pr-6">
        <Stack
          className={classNames(!isInEditMode ? 'translate-y-3' : '', 'duration-200')}
          marginTop="auto"
          paddingY="4px"
          direction="row" 
          alignItems="center"
        >
          <Checkbox
            disabled={!isInEditMode}
            checked={getValues(`contacts.${contactIndex}.isPrimary`) ?? false}
            onChange={(_, isChecked) => handlerIsPrimaryChange(isChecked)}
          />
          <label className='text-sm text-label'>Is Primary Contact</label>
        </Stack>
      </div>
      <div className="flex grow -translate-x-4 flex-col items-end justify-end">
        <IconButton
          disabled={!isInEditMode}
          onClick={handleDeleteContact}
          className='disabled:invisible'
        >
          <TrashIcon className='fill-icon'/>
        </IconButton>
      </div>
    </div>
  );
};